/**
 * Catálogo de vacinas (#530). Global: todos leem, só quem administra a plataforma escreve
 * (spec D8). A lista é curta e completa — sem paginação nem filtro.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { DataTable, PageShell, RouteActionButton, type DataTableColumn } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { vaccineTypeService } from '../../services/vaccineTypeService';
import { getApiErrorMessage } from '../../types/common';
import { resumoDoEsquema, resumoDoReforco, type VaccineType } from '../../types/vaccination';

const columns: DataTableColumn<VaccineType>[] = [
  {
    id: 'name',
    label: 'Vacina',
    primary: true,
    render: (vacina) => (
      <Box>
        <Typography variant="body2" fontWeight="medium">
          {vacina.name}
        </Typography>
        {vacina.description && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
            {vacina.description}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    id: 'scheduleMonths',
    label: 'Esquema',
    render: (vacina) => <Typography variant="body2">{resumoDoEsquema(vacina.scheduleMonths)}</Typography>,
  },
  {
    id: 'boosterMonths',
    label: 'Reforço',
    render: (vacina) => <Typography variant="body2">{resumoDoReforco(vacina.boosterMonths)}</Typography>,
  },
  {
    id: 'status',
    label: 'Situação',
    render: (vacina) => (
      <Chip
        label={vacina.status === 'ACTIVE' ? 'Ativa' : 'Inativa'}
        size="small"
        color={vacina.status === 'ACTIVE' ? 'success' : 'default'}
      />
    ),
  },
];

export const VaccineTypeListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/vaccine-types/:id/edit', pode('vaccine-type.update'), 'Editar');
  const excluir = useAcao('vaccine-type.delete', 'Excluir');
  const [vacinas, setVacinas] = useState<VaccineType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setVacinas(await vaccineTypeService.getAll());
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar o catálogo de vacinas'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Vacina com dose ou exigência não se exclui: o servidor recusa com "inative em vez de
  // excluir", e o texto basta — é estado impossível, não pré-requisito que falta.
  const handleDelete = async (vacina: VaccineType) => {
    if (!window.confirm(`Excluir a vacina "${vacina.name}" do catálogo?`)) return;

    try {
      setError(null);
      await vaccineTypeService.delete(vacina.id);
      carregar();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao excluir a vacina'));
    }
  };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Vacinas' }]}
      title="Catálogo de vacinas"
      subtitle="Esquema de doses e reforço de cada vacina. As empresas escolhem daqui as que exigem."
      actions={
        <RouteActionButton
          para="/vaccine-types/new"
          exige={pode('vaccine-type.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Nova vacina
        </RouteActionButton>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <DataTable
        columns={columns}
        rows={vacinas}
        getRowId={(vacina) => vacina.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma vacina cadastrada no catálogo ainda',
          description:
            "Cada vacina guarda os meses das doses do esquema e o intervalo de reforço, e é dela que cada empresa marca como exigidas as que cobra. Quem administra a plataforma cadastra pelo botão 'Nova vacina'.",
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: (vacina) => navigate(`/vaccine-types/${vacina.id}/edit`),
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDelete,
            ...excluir,
          },
        ]}
      />
    </PageShell>
  );
};

export default VaccineTypeListPage;
